import { DateTime } from 'luxon';

import { generateCorrectSlug } from '../generate-correct-slug';
import { FavouriteType } from '@/context/FavouritesContext';
import {
  EditorialCard,
  LeisurePackageOfferCard,
  ListCardCategories,
  ListCardsCategories,
  ListCardType,
  OccationEventCard,
  OccationNewsCard,
  OccationOfferCard,
  ServiceCard,
  SubTenantCard,
  TenantCard,
} from './interfaces/list-card.interface';
import { IMeetingPlacePageEventItem } from '@/lib/fragments/meetingPlacePageEventItem';
import { IMeetingPlacePageOfferItem } from '@/lib/fragments/meetingPlacePageOfferItem';
import { TTenantTypes } from '@/lib/fragments/meetingPlacePageTenantList';
import { IMeetingPlacePageNewsItem } from '@/lib/fragments/meetingPlacePageNewsItem';
import { IMeetingPlacePageEditorial } from '@/lib/fragments/meetingPlacePageEditorial';
import { TOccationTypes } from '@/lib/fragments/meetingPlacePageOccations';
import { IMeetingPlacePageServiceItem } from '@/lib/fragments/meetingPlacePageServiceItem';
import { _numTag } from './list-page-constants';
import { IMeetingPlacePageLeisurePackageOfferItem } from '@/lib/fragments/meetingPlacePageLeisurePackageOfferItem';
import { ILibraryCuisineType } from '@/lib/fragments/libraryCuisineType';
import {
  pageIsEditorial,
  pageIsEvent,
  pageIsLeisurePackageOffer,
  pageIsOffer,
  pageIsService,
  pageIsTenantEntertainment,
  pageIsTenantFoodBeverage,
  pageIsTenantShop,
} from './type-guards';
import client, { getApolloClient, getBrowserClient } from '@/lib/apolloClient';
import { ILibraryMeetingPlaceDictionary } from '@/lib/fragments/libraryMeetingPlaceDictionary';
import { getNowInZone, getZoneFromDate } from '../countdown';
import { isBrowser } from '../is-browser';

export interface SelectInputNameProps {
  news?: string;
  events?: string;
}

interface SlotDay {
  date: string;
  timeSlots: Array<{ startTime: string; endTime: string }>;
}

export const getEventCategories = (
  eventItem: IMeetingPlacePageEventItem,
): ListCardsCategories[] => {
  return eventItem.eventTypesCollection?.items?.filter(Boolean)?.map((et) => {
    return { id: et?.sys?.id, name: et?.name };
  });
};

export const getNextSlotText = (
  slots: SlotDay[] | undefined,
  node_locale?: string,
  startDate?: any,
): { variant: string; date?: any } | null => {
  if (!slots || slots.length === 0) return null;

  const zone = getZoneFromDate(startDate.toString());
  const current = getNowInZone(zone);

  console.log('current value is', current.toISODate());

  const hasToday = slots.some(
    (s) => s.date === current.toISODate() && s.timeSlots?.length > 0,
  );

  if (hasToday) {
    return { variant: 'Today' };
  }

  // Find the next future slot
  const nextSlot = slots
    .filter((s) => {
      const slotDate = DateTime.fromISO(s.date).startOf('day');
      return slotDate > current && s.timeSlots?.length > 0;
    })
    .sort(
      (a, b) =>
        DateTime.fromISO(a.date).toMillis() -
        DateTime.fromISO(b.date).toMillis(),
    )[0];

  if (!nextSlot) return null;

  const nextDate = new Date(nextSlot.date);
  const formattedDate = nextDate.toLocaleDateString(node_locale, {
    day: 'numeric',
    month: 'short',
  });

  return { variant: 'nextEvent', date: formattedDate };
};

export const getOfferCategories = (
  offerItem: IMeetingPlacePageOfferItem,
): ListCardsCategories[] => {
  if (offerItem) {
    return [
      {
        id: offerItem?.sys?.id,
        name: offerItem?.livliFamily
          ? offerItem?.livliFamily
          : offerItem?.typeOfOffer?.name,
      },
    ];
  } else {
    return [];
  }
};

export const getTenantCardCategories = (
  tenantPage: TTenantTypes,
): ListCardsCategories[] => {
  const tenantCategories: ListCardsCategories[] = [];

  const getSecondaryCategories = (
    secondaryCategories: ListCardCategories[],
  ): ListCardsCategories[] => {
    return secondaryCategories?.filter(Boolean)?.map((category) => {
      return {
        id: category?.sys?.id,
        name: category?.name,
      };
    });
  };

  // Categories from tenant page
  if (tenantPage && tenantPage?.primaryCategory) {
    tenantCategories.push({
      id: tenantPage?.primaryCategory?.sys?.id,
      name: tenantPage?.primaryCategory?.name,
    });
  }
  if (tenantPage?.secondaryCategoriesCollection?.items) {
    tenantCategories.push(
      ...getSecondaryCategories(
        tenantPage?.secondaryCategoriesCollection?.items,
      ),
    );
  }

  // including brand categories
  if (tenantPage && tenantPage?.brand?.primaryCategory) {
    tenantCategories.push({
      id: tenantPage?.brand?.primaryCategory?.sys?.id,
      name: tenantPage?.brand?.primaryCategory?.name,
    });
  }
  if ((tenantPage?.brand as any)?.secondaryCategoriesCollection?.items) {
    tenantCategories.push(
      ...getSecondaryCategories(
        (tenantPage?.brand as any)?.secondaryCategoriesCollection?.items,
      ),
    );
  }

  // removing duplicate if any
  return tenantCategories.filter(
    (category, index, self) =>
      index === self.findIndex((c) => c.id === category?.id),
  );
};

export const mergeTenantListItems = <T extends { sys?: { id?: string } }>(
  tenantListItems: unknown[] | undefined,
  directListItems: T[] | undefined,
  typeGuard: (item: any) => item is T,
): T[] => {
  const items =
    tenantListItems?.filter(Boolean)?.filter((item) => typeGuard(item)) ?? [];
  const mergedItems = [...items];

  directListItems?.filter(Boolean)?.forEach((item) => {
    const alreadyExists = mergedItems.some(
      (mergedItem) => mergedItem?.sys?.id === item?.sys?.id,
    );
    if (!alreadyExists) mergedItems.push(item);
  });

  return mergedItems;
};

const getTenantCardPrimaryCategories = (
  tenantPage: TTenantTypes,
): ListCardsCategories[] => {
  const tenantPrimaryCategories: ListCardsCategories[] = [];

  if (tenantPage && tenantPage?.primaryCategory) {
    tenantPrimaryCategories.push({
      id: tenantPage?.primaryCategory?.id,
      name: tenantPage?.primaryCategory?.name,
    });
  } else {
    //Fallback
    if (tenantPage && tenantPage?.brand?.primaryCategory)
      tenantPrimaryCategories.push({
        id: tenantPage?.brand?.primaryCategory?.id,
        name: tenantPage?.brand?.primaryCategory?.name,
      });
  }
  return tenantPrimaryCategories;
};

const createTenantCard = (
  page: TTenantTypes,
  urlSubdirectory?: string | null,
  node_locale?: string,
  prefix?: string,
): TenantCard => {
  const name = page?.name
    ? page?.name
    : page?.brand?.name
      ? page?.brand.name
      : '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));
  const pageSlug = page?.slug ? page?.slug : '';
  const pageNodeLocale = (page as any).sys.locale || node_locale || '';

  return {
    id: page?.sys?.id || '',
    name,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page?.coverImage ? page?.coverImage : page?.brand?.coverImage,
    slug: generateCorrectSlug(
      {
        customizedSlug: page?.customizedSlug,
        name,
        node_locale: pageNodeLocale,
        slug: pageSlug,
      },
      prefix ?? '',
      urlSubdirectory,
    ),
    categories: getTenantCardCategories(page),
    logo: page?.logo ? page?.logo : page?.brand?.logo,
    cardType: 'tenant',
    favouriteType: ((): FavouriteType => {
      switch (page.__typename) {
        case 'MeetingPlacePageTenantShop':
          return 'shop';
        case 'MeetingPlacePageTenantFoodBeverage':
          return 'foodAndBeverage';
        case 'MeetingPlacePageTenantEntertainment':
          return 'entertainment';
        default:
          return 'shop';
      }
    })(),
    node_locale: page?.sys?.locale,
    giftCard: page?.giftCard,
    loyaltyCard: page?.loyaltyCard,
    ...('cuisine' in page && {
      cuisineTypes: page?.cuisine?.map((ct: ILibraryCuisineType) => ({
        id: ct.id,
        name: ct.name,
      })),
    }),
    ...('priceLevel' in page && {
      priceLevel: {
        id: page?.priceLevel?.id,
        name: page?.priceLevel?.name,
      },
    }),
    ...('hasOffer' in page && {
      offerPage: page?.hasOffer,
    }),
    ...('offerCampaignNames' in page && {
      offerCampaignNames: page?.offerCampaignNames as string[],
    }),
    ...('hasEvent' in page && {
      eventPage: page?.hasEvent,
    }),
  };
};

export const createSubTenantCard = (
  page: TTenantTypes,
  urlSubdirectory?: string | null,
  node_locale?: string,
  prefix?: string,
): SubTenantCard => {
  const name = page?.name
    ? page?.name
    : page?.brand?.name
      ? page?.brand.name
      : '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));
  const pageSlug = page?.slug ? page?.slug : '';
  const pageNodeLocale = (page as any).sys.locale || node_locale || '';

  return {
    id: page?.sys?.id || '',
    name,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page?.coverImage ? page?.coverImage : page?.brand?.coverImage,
    slug: generateCorrectSlug(
      {
        customizedSlug: page?.customizedSlug,
        name,
        node_locale: pageNodeLocale,
        slug: pageSlug,
      },
      prefix ?? '',
      urlSubdirectory,
    ),
    categories: getTenantCardPrimaryCategories(page),
    logo: page?.logo ? page?.logo : page?.brand?.logo,
    cardType: 'subTenant',
    node_locale: page?.sys?.locale,
  };
};

const createOfferCard = (
  page: IMeetingPlacePageOfferItem,
  urlSubdirectory?: string | null,
  node_locale?: string,
  prefix?: string,
): OccationOfferCard => {
  const name = page?.name || '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));
  const pageNodeLocale = (page as any)?.node_locale || node_locale || '';
  return {
    id: page?.sys?.id || '',
    name,
    updatedAt: page?.updatedAt,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page?.coverImage,
    date: DateTime.fromISO(page?.date),
    slug: generateCorrectSlug(
      {
        customizedSlug: (page as any).customizedSlug,
        name,
        // node_locale: page?.node_locale,
        node_locale: pageNodeLocale,
        slug: page?.slug,
      },
      prefix ?? '',
      urlSubdirectory,
    ),
    validFrom: page?.validFrom,
    validTo: page?.validTo,
    priority: page?.priority,
    categories: getOfferCategories(page),
    typeOfOffer: page?.typeOfOffer,
    tags:
      page?.offerCampaignTagsCollection?.items
        ?.filter(Boolean)
        ?.map((tag, index) => ({
          id: `${page?.sys?.id || ''}-${index}`,
          tagName: tag?.tagName || '',
          colour: tag?.colour || '',
        })) || [],
    logo: page?.tenant?.logo
      ? page?.tenant.logo
      : page?.tenant?.brand?.logo
        ? page?.tenant.brand.logo
        : undefined,
    cardType: 'offer',
    brand: page?.tenant?.name
      ? page?.tenant.name
      : page?.tenant?.brand?.name
        ? page?.tenant.brand.name
        : '',
    node_locale: pageNodeLocale,
  };
};

const createEventCard = (
  page: IMeetingPlacePageEventItem,
  selectInputName?: SelectInputNameProps,
  timeZone?: string,
  urlSubdirectory?: string | null,
  node_locale?: string,
  prefix?: string,
): OccationEventCard => {
  const name = page?.name || '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));
  const pageNodeLocale = (page as any).node_locale || node_locale || '';
  return {
    id: page?.sys?.id || '',
    name,
    updatedAt: page?.updatedAt,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page?.mediaField?.jsonBynderAsset
      ? {
          url: page?.mediaField.jsonBynderAsset.originalUrl,
          description: '',
          alt: page?.mediaField?.altText || '',
          contentType: 'image',
          filename: '',
          height: '',
          width: '',
          title: '',
        }
      : {
          url: '',
          description: '',
          contentType: 'image',
          filename: '',
          height: '',
          width: '',
          title: '',
        },
    caption: page?.caption,
    slug: generateCorrectSlug(
      {
        customizedSlug: page?.customizedSlug,
        name,
        // node_locale: page?.node_locale,
        node_locale: pageNodeLocale,
        slug: page?.slug,
      },
      prefix ?? '',
      urlSubdirectory,
    ),
    startDate: page?.startDate,
    endDate: page?.endDate,
    hideInListFrom: DateTime.fromISO(page?.hideInListFrom),
    eventTypes: page?.eventTypesCollection?.items,
    floorPlanId: page?.floorPlanId,
    categories: selectInputName?.events
      ? [
          {
            id: page?.sys?.id,
            name: selectInputName?.events ? selectInputName?.events : '',
          },
        ]
      : getEventCategories(page),
    logo: page?.tenant?.logo
      ? page?.tenant.logo
      : page?.tenant?.brand?.logo
        ? page?.tenant.brand.logo
        : undefined,
    cardType: 'event',
    node_locale: pageNodeLocale,
    priority: page?.priority,
    slots: getNextSlotText(
      page?.slots,
      pageNodeLocale,
      page?.startDate ?? page?.endDate,
    ),
  };
};

const createNewsCard = (
  page: IMeetingPlacePageNewsItem,
  selectInputName?: SelectInputNameProps,
  urlSubdirectory?: string | null,
  node_locale?: string,
  prefix?: string,
): OccationNewsCard => {
  const name = page?.name || '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));
  const pageNodeLocale = (page as any)?.node_locale || node_locale || '';
  return {
    id: page?.sys?.id,
    name,
    isdatevisible: page?.isdatevisible,
    updatedAt: page?.updatedAt,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page?.coverImage,
    slug: generateCorrectSlug(
      {
        // customizedSlug: page?.customizedSlug,
        customizedSlug: (page as any)?.customizedSlug,
        name,
        node_locale: pageNodeLocale,
        // node_locale: page?.node_locale,
        slug: page?.slug,
      },
      prefix ?? '',
      urlSubdirectory,
    ),
    date: DateTime.fromISO(page?.date),
    title: page?.name,
    caption: page?.caption,
    hideInListFrom: DateTime.fromISO(page?.hideInListFrom),
    categories: [
      {
        id: page?.sys?.id,
        name: selectInputName?.news ? selectInputName?.news : '',
      },
    ],
    logo: page?.tenant?.logo
      ? page?.tenant.logo
      : page?.tenant?.brand?.logo
        ? page?.tenant.brand.logo
        : undefined,
    cardType: 'news',
    // node_locale: page?.node_locale,
    node_locale: pageNodeLocale,
  };
};

const createServiceCard = (
  page: IMeetingPlacePageServiceItem,
  urlSubdirectory?: string | null,
  node_locale?: string,
  prefix?: string,
): ServiceCard => {
  const serviceType = page?.serviceType;

  const firstCharIsNum =
    !isNaN(parseInt(serviceType?.name.slice(0, 1))) || false;
  const pageNodeLocale = page?.sys?.locale || node_locale || '';
  const categories = serviceType?.serviceCategory
    ? [
        {
          id: serviceType?.serviceCategory?.sys?.id,
          name: serviceType?.serviceCategory?.name,
        },
      ]
    : [];
  return {
    id: page?.sys?.id,
    name: page?.name
      ? page?.name
      : page?.serviceType?.name
        ? page?.serviceType.name
        : '',
    firstChar: firstCharIsNum ? _numTag : serviceType?.name.slice(0, 1),
    firstCharIsNum,
    imageWrapper: serviceType?.coverImageWrapper,
    slug: generateCorrectSlug(
      {
        customizedSlug: page?.customizedSlug,
        name: page?.name,
        node_locale: pageNodeLocale,
        slug: page?.slug,
      },
      prefix ?? '',
      urlSubdirectory,
    ),
    logo: undefined,
    categories: categories,
    cardType: 'service',
    node_locale: pageNodeLocale,
  };
};

const createEditorialCard = (
  page: IMeetingPlacePageEditorial,
  urlSubdirectory?: string | null,
  node_locale?: string,
  prefix?: string,
): EditorialCard => {
  const name = page?.name || '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));
  const pageNodeLocale = (page as any).node_locale || node_locale || '';

  return {
    id: page?.sys?.id || '',
    name,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page?.mediaField?.jsonBynderAsset
      ? {
          url: page?.mediaField.jsonBynderAsset.originalUrl,
          description: '',
          alt: page?.mediaField?.altText || '',
          contentType: 'image',
          filename: '',
          height: '',
          width: '',
          title: '',
        }
      : {
          url: '',
          description: '',
          contentType: 'image',
          filename: '',
          height: '',
          width: '',
          title: '',
        },
    slug: generateCorrectSlug(
      {
        customizedSlug: (page as any).customizedSlug,
        name,
        node_locale: pageNodeLocale,
        slug: page?.slug,
      },
      prefix ?? '',
      urlSubdirectory,
    ),
    title: page?.name,
    categories: [],
    cardType: 'editorial',
    node_locale: page?.node_locale,
    tags: page?.tagsCollection?.items?.filter(Boolean)?.map((tag) => ({
      id: tag?.sys?.id || '',
      tagName: tag?.tagName || '',
      colour: tag?.colour || '',
    })),
  };
};

const createLeisurePackageOfferCard = (
  page: IMeetingPlacePageLeisurePackageOfferItem,
  urlSubdirectory?: string | null,
  node_locale?: string,
  prefix?: string,
): LeisurePackageOfferCard => {
  const name = page?.name || '';
  const firstCharIsNum = !isNaN(parseInt(name.slice(0, 1)));
  const pageNodeLocale = (page as any).node_locale || node_locale || '';

  return {
    id: page?.sys.id,
    name,
    titleLeisurePackageOffer: page?.titleLeisurePackageOffer,
    firstChar: firstCharIsNum ? _numTag : name.slice(0, 1),
    firstCharIsNum,
    image: page?.coverImage,
    slug: generateCorrectSlug(
      {
        customizedSlug: (page as any).customizedSlug,
        name,
        node_locale: pageNodeLocale,
        slug: page?.slug,
      },
      prefix ?? '',
      urlSubdirectory,
    ),
    cardTag: page?.cardTag,
    offerValidFrom: DateTime.fromISO(page?.offerValidFrom || ''),
    offerValidTill: DateTime.fromISO(page?.offerValidTill || ''),
    categories: [],
    salesPrice: page?.salesPrice,
    originalPrice: page?.originalPrice,
    currency: page?.currency,
    cardType: 'leisurePackageOffer',
    node_locale: pageNodeLocale,
    lpoTags: page?.leisurePackageOfferTagsCollection?.items,
  };
};

export const createListCardArray = (
  list:
    | TTenantTypes[]
    | TOccationTypes[]
    | IMeetingPlacePageServiceItem[]
    | IMeetingPlacePageEditorial[]
    | IMeetingPlacePageLeisurePackageOfferItem[],
  selectInputName?: SelectInputNameProps,
  timeZone?: string,
  urlSubdirectory?: string | null,
  node_locale?: string,
  prefix?: string,
): ListCardType[] => {
  return list?.map((page) =>
    getCardArray(
      page,
      selectInputName,
      timeZone,
      urlSubdirectory,
      node_locale,
      prefix,
    ),
  );
};

export const calculateActiveFilters = (
  searchString: string | null,
  selectedCategory?: string | null,
  selectedType?: string | null,
  selectedPriceLevel?: string | null,
  filterGiftCard?: boolean | null,
  filterLoyaltyCard?: boolean | null,
  filterOffer?: boolean | null,
  filterTag?: string | null,
  filterMemberDayCard?: boolean | null,
) => {
  let activeFilters = 0;
  if (searchString && searchString.length > 0) {
    activeFilters++;
  }
  if (selectedCategory && selectedCategory.length > 0) {
    activeFilters++;
  }
  if (selectedType && selectedType.length > 0) {
    activeFilters++;
  }
  if (selectedPriceLevel && selectedPriceLevel.length > 0) {
    activeFilters++;
  }
  if (
    filterGiftCard ||
    filterLoyaltyCard ||
    filterOffer ||
    filterMemberDayCard
  ) {
    activeFilters++;
  }
  if (filterTag && filterTag.length > 0) {
    activeFilters++;
  }
  return activeFilters;
};

export const getCardArray = (
  page:
    | TTenantTypes
    | TOccationTypes
    | IMeetingPlacePageServiceItem
    | IMeetingPlacePageEditorial
    | IMeetingPlacePageLeisurePackageOfferItem,
  selectInputName?: SelectInputNameProps,
  timeZone?: string,
  urlSubdirectory?: string | null,
  node_locale?: string,
  prefix?: string,
):
  | TenantCard
  | OccationEventCard
  | OccationNewsCard
  | OccationOfferCard
  | ServiceCard
  | EditorialCard
  | LeisurePackageOfferCard => {
  if (
    pageIsTenantShop(page) ||
    pageIsTenantEntertainment(page) ||
    pageIsTenantFoodBeverage(page)
  ) {
    return createTenantCard(page, urlSubdirectory, node_locale, prefix);
  }

  if (pageIsOffer(page)) {
    return createOfferCard(page, urlSubdirectory, node_locale, prefix);
  }

  if (pageIsEvent(page)) {
    return createEventCard(
      page,
      selectInputName,
      timeZone,
      urlSubdirectory,
      node_locale,
      prefix,
    );
  }
  if (pageIsService(page)) {
    return createServiceCard(page, urlSubdirectory, node_locale, prefix);
  }
  if (pageIsEditorial(page)) {
    return createEditorialCard(page, urlSubdirectory, node_locale, prefix);
  }
  if (pageIsLeisurePackageOffer(page)) {
    return createLeisurePackageOfferCard(
      page,
      urlSubdirectory,
      node_locale,
      prefix,
    );
  }
  return createNewsCard(
    page,
    selectInputName,
    urlSubdirectory,
    node_locale,
    prefix,
  );
};

export const fetchListingData = async (
  query: any,
  path: string,
  id?: string,
  node_locale?: string,
  preview?: boolean,
  limit: number = 3,
  currentDate?: string,
) => {
  // const limit = 100;
  let skip = 0;
  const allItems: any[] = [];
  let total = 0;

  let mergedItems: any[] = [];
  let completeData: any = null;
  // Preview runs through the same-origin /api/graphql proxy in the browser
  // (token never exposed); on the server it calls Contentful directly.
  const mpclient = isBrowser
    ? getBrowserClient(preview)
    : preview
      ? getApolloClient(preview)
      : client;

  do {
    const res: any = await mpclient.query({
      query,
      variables: {
        // id: id,
        limit,
        skip,
        ...(id && { id: id }),
        ...(node_locale && { locale: node_locale }),
        ...(preview && { preview: preview }),
        ...(currentDate && { currentDate: currentDate }),
      },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

    const currentData = path
      .split('.')
      .reduce((acc: any, key: string) => acc?.[key], res?.data);

    if (!currentData) break;

    if (!completeData) {
      // Get top-level object → path before last key
      const parentPath = path.split('.').slice(0, -1).join('.');

      completeData = parentPath
        .split('.')
        .reduce((acc: any, key: string) => acc?.[key], res?.data);
    }
    mergedItems = [...mergedItems, ...(currentData.items || [])];
    total = currentData.total;
    skip += limit;
  } while (mergedItems.length < total);

  if (completeData) {
    const lastKey: any = path.split('.').pop();
    completeData[lastKey] = {
      ...completeData[lastKey],
      items: mergedItems,
      total,
    };
  }

  return completeData;
};

/**
 * Serializes DateTime objects in list cards to ISO strings for Client Components.
 * DateTime objects cannot be passed to Client Components from Server Components,
 * so this function converts them to ISO strings.
 */
export const serializeListCardDateTimes = (
  cardList: ListCardType[],
): ListCardType[] => {
  // Helper to check if a value is a Luxon DateTime object
  // Checks multiple indicators to catch all DateTime instances
  const isDateTime = (dt: any): boolean => {
    if (!dt) return false;
    if (typeof dt === 'string') return false;
    if (typeof dt === 'number') return false;
    if (dt instanceof Date) return false; // Native Date objects are fine

    // Check for Luxon DateTime indicators
    // DateTime objects have: isLuxonDateTime, _zone, ts, toISO method
    // If it has toISO method and looks like a DateTime (has _zone or ts), it's a DateTime
    if (typeof dt.toISO === 'function') {
      return (
        dt.isLuxonDateTime === true ||
        typeof dt._zone !== 'undefined' ||
        typeof dt.ts !== 'undefined' ||
        typeof dt.loc !== 'undefined'
      );
    }

    return false;
  };

  // Helper to safely convert DateTime to ISO string
  const toISOString = (dt: any): string | undefined => {
    if (!dt) return undefined;
    if (typeof dt === 'string') return dt;
    if (typeof dt === 'number') return undefined;

    // If it's a DateTime object, we MUST convert it
    if (isDateTime(dt)) {
      try {
        const iso = dt.toISO();
        // If toISO returns null or undefined, try toString as fallback
        if (iso) return iso;
        // Try toISOString as alternative
        if (typeof dt.toISOString === 'function') {
          const isoStr = dt.toISOString();
          if (isoStr) return isoStr;
        }
        return dt.toString();
      } catch (e) {
        // If conversion fails, return undefined (better than passing DateTime object)
        console.warn('Failed to serialize DateTime object:', e);
        return undefined;
      }
    }

    return undefined;
  };

  return cardList.map((card: any) => {
    const serialized: any = { ...card };

    // Process known DateTime fields based on card type
    if (card.cardType === 'news') {
      // Always convert if it's a DateTime object, even if result is undefined
      if (isDateTime(card.date)) {
        serialized.date = toISOString(card.date) ?? undefined;
      }
      if (isDateTime(card.hideInListFrom)) {
        serialized.hideInListFrom =
          toISOString(card.hideInListFrom) ?? undefined;
      }
    } else if (card.cardType === 'event') {
      if (isDateTime(card.startDate)) {
        serialized.startDate = toISOString(card.startDate) ?? undefined;
      }
      if (isDateTime(card.endDate)) {
        serialized.endDate = toISOString(card.endDate) ?? undefined;
      }
      if (isDateTime(card.hideInListFrom)) {
        serialized.hideInListFrom =
          toISOString(card.hideInListFrom) ?? undefined;
      }
    } else if (card.cardType === 'offer') {
      if (isDateTime(card.date)) {
        serialized.date = toISOString(card.date) ?? undefined;
      }
      if (isDateTime(card.validFrom)) {
        serialized.validFrom = toISOString(card.validFrom) ?? undefined;
      }
      if (isDateTime(card.validTo)) {
        serialized.validTo = toISOString(card.validTo) ?? undefined;
      }
    } else if (card.cardType === 'leisurePackageOffer') {
      if (isDateTime(card.offerValidFrom)) {
        serialized.offerValidFrom =
          toISOString(card.offerValidFrom) ?? undefined;
      }
      if (isDateTime(card.offerValidTill)) {
        serialized.offerValidTill =
          toISOString(card.offerValidTill) ?? undefined;
      }
    }

    // Defensive: Also check common DateTime field names that might exist on any card
    const commonDateTimeFields = [
      'date',
      'startDate',
      'endDate',
      'validFrom',
      'validTo',
      'hideInListFrom',
      'offerValidFrom',
      'offerValidTill',
    ];

    commonDateTimeFields.forEach((field) => {
      if (isDateTime(serialized[field])) {
        serialized[field] = toISOString(serialized[field]) ?? undefined;
      }
    });

    return serialized;
  });
};

